import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { add, format, parse } from "date-fns";
import { useAtomValue } from "jotai";
import { useCallback } from "react";
import { ScrollView } from "react-native";
import { useFragment } from "react-relay";

import { useDepartmentMaxGolfCourseBookingWindow } from "@/features/Department/hooks/useDepartmentMaxGolfCourseBookingWindow";
import { useGetClubStartOfWeekNumberPatron } from "@/hooks/useGetClubStartOfWeekNumberPatron";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { Box, VStack } from "@whoosh/design-system/src/components/primitive";
import { MobileDateSwitcher } from "@whoosh/design-system/src/patterns/patronApp/MobileDateSwitcher/MobileDateSwitcher";

import { courseFilterAtom } from "../../atoms/courseFilterAtom";
import { CourseList_department$key } from "./__generated__/CourseList_department.graphql";
import { CourseCard } from "./components/CourseCard";

type Props = {
  departmentRef: CourseList_department$key | null;
};
export const CourseList = ({ departmentRef }: Props) => {
  const selectedCourses = useAtomValue(courseFilterAtom);
  const isMobile = useResponsiveProp({
    base: 1,
    sm: 0,
  });
  const navigation =
    useNavigation<
      NavigationProp<ClubStackNavigatorParamList, "DepartmentAvailability">
    >();

  const route =
    useRoute<
      RouteProp<ClubStackNavigatorParamList, "DepartmentAvailability">
    >();

  const date = route?.params?.date;
  const department = useFragment(CourseListFragmentString, departmentRef);
  const courses =
    department?.courses?.edges?.flatMap((e) =>
      e?.node &&
      (selectedCourses.includes(e.node.id) || selectedCourses.length === 0)
        ? [e.node]
        : []
    ) || [];

  const sortedCourses = courses.sort((a, b) => {
    const aName = `${a.facility.name} - ${a.name}`;
    const bName = `${b.facility.name} - ${b.name}`;
    if (aName < bName) {
      return -1;
    } else if (aName > bName) {
      return 1;
    } else {
      return 0;
    }
  });

  const clubWeekStartsOn = useGetClubStartOfWeekNumberPatron(
    department?.club ?? null
  );
  const bookingWindowDays = useDepartmentMaxGolfCourseBookingWindow(
    department ?? null
  );
  const today = format(new Date(), "yyyy-MM-dd");
  const endDate = format(
    add(parse(today, "yyyy-MM-dd", new Date()), {
      days: bookingWindowDays,
    }),
    "yyyy-MM-dd"
  );

  const onSelectDate = useCallback(
    (selectedDate: string) => {
      navigation.setParams({
        date: selectedDate,
      });
    },
    [navigation]
  );

  return (
    <ScrollView style={{ backgroundColor: "white" }}>
      <Box
        flex={1}
        backgroundColor="surface-primary"
        paddingHorizontal={6}
        paddingBottom={6}
        paddingTop={{ base: 0, sm: 6 }}
      >
        {isMobile && date ? (
          <MobileDateSwitcher
            startDate={today}
            endDate={endDate}
            selectedDate={date}
            onChange={onSelectDate}
            clubWeekStartsOn={clubWeekStartsOn}
          />
        ) : null}
        <VStack gap={10} paddingTop={{ base: 4, sm: 0 }}>
          {sortedCourses.map((course, i) => (
            <CourseCard key={course.id} courseRef={course} index={i} />
          ))}
        </VStack>
      </Box>
    </ScrollView>
  );
};

const CourseListFragmentString = graphql`
  fragment CourseList_department on Department {
    id
    club {
      ...useGetClubStartOfWeekNumberPatron_club
    }
    ...useDepartmentMaxGolfCourseBookingWindow_department
    courses(facilityType: GOLF_COURSE, archived: false) {
      edges {
        node {
          id
          name
          facility {
            name
          }
          ...CourseCard_course
        }
      }
    }
  }
`;
